import { getMessaging, getToken, isSupported, Messaging, onMessage } from 'firebase/messaging';
import { getPortalFirebaseApp } from './firebase';
import { env } from '../config/runtimeEnv';

export interface FcmRealtimeEnvelope<TPayload = Record<string, unknown>> {
  channel: string;
  eventType: string;
  updatedAt?: string;
  payload: TPayload;
}

const FCM_TOKEN_STORAGE_KEY = 'portal_fcm_token';

let messagingInstance: Messaging | null = null;
let warnedUnsupported = false;
let warnedMissingConfig = false;

const getMessagingInstance = async (): Promise<Messaging | null> => {
  if (!(await isSupported())) {
    if (!warnedUnsupported) {
      warnedUnsupported = true;
      console.warn('FCM is not supported in this browser.');
    }
    return null;
  }

  if (messagingInstance) return messagingInstance;

  const app = getPortalFirebaseApp();
  if (!app) {
    if (!warnedMissingConfig) {
      warnedMissingConfig = true;
      console.warn('FCM is disabled: missing REACT_APP_FIREBASE_* configuration.');
    }
    return null;
  }

  messagingInstance = getMessaging(app);
  return messagingInstance;
};

export const getStoredFcmToken = (): string | null => localStorage.getItem(FCM_TOKEN_STORAGE_KEY);

export const storeFcmToken = (token: string): void => {
  localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
};

export const clearStoredFcmToken = (): void => {
  localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
};

export const requestFcmToken = async (): Promise<string | null> => {
  if (typeof window === 'undefined') return null;
  if (!('Notification' in window)) return null;

  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  const vapidKey = env.FIREBASE_VAPID_KEY;
  if (!vapidKey) {
    console.warn('FCM token registration skipped: FIREBASE_VAPID_KEY is missing from runtime/build env.');
    return null;
  }

  if (Notification.permission === 'denied') return null;

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
  if (permission !== 'granted') return null;

  try {
    const token = await getToken(messaging, { vapidKey });
    return token || null;
  } catch (error) {
    console.warn('Failed to get FCM token:', (error as Error).message);
    return null;
  }
};

export const subscribeToFcmMessages = (
  onEnvelope: (envelope: FcmRealtimeEnvelope) => void,
): (() => void) => {
  let unsubscribe = () => { };
  let isDisposed = false;
  let onServiceWorkerMessage: ((event: MessageEvent) => void) | null = null;

  void (async () => {
    const messaging = await getMessagingInstance();
    if (!messaging || isDisposed) return;
    unsubscribe = onMessage(messaging, (message) => {
      const data = message.data || {};
      if (!data.channel || !data.eventType || !data.payload) return;

      try {
        const payload =
          typeof data.payload === 'string' ? JSON.parse(data.payload) : data.payload;
        onEnvelope({
          channel: String(data.channel),
          eventType: String(data.eventType),
          updatedAt: data.updatedAt ? String(data.updatedAt) : undefined,
          payload,
        });
      } catch (error) {
        console.warn('Failed to parse FCM realtime payload:', (error as Error).message);
      }
    });

    if ('serviceWorker' in navigator) {
      onServiceWorkerMessage = (event: MessageEvent) => {
        const data = event?.data;
        if (!data?.__fcmEnvelope || !data?.data) return;
        const swData = data.data as Record<string, unknown>;
        const payloadRaw = swData.payload;
        const payload =
          typeof payloadRaw === 'string'
            ? (() => {
              try {
                return JSON.parse(payloadRaw);
              } catch {
                return null;
              }
            })()
            : payloadRaw;
        if (!swData.channel || !swData.eventType || !payload) return;
        onEnvelope({
          channel: String(swData.channel),
          eventType: String(swData.eventType),
          updatedAt: swData.updatedAt ? String(swData.updatedAt) : undefined,
          payload: payload as Record<string, unknown>,
        });
      };
      navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);
    }
  })();

  return () => {
    isDisposed = true;
    unsubscribe();
    if (onServiceWorkerMessage && 'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
    }
  };
};

